import React from 'react'

import { INRWorkoutSessionHit } from 'interfaces/workout'

import './hit-location-surface.scss'

export const ANGLE_DIVIDERS = [-135, -45, 45, 135] as const
export const HEIGHT_DIVIDERS = [1 / 3, 2 / 3] as const

export interface HitLocationPosition {
  left: number
  outOfRange: boolean
  top: number
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const wrapAngle = (angle: number): number => {
  if (angle >= -180 && angle <= 180) return angle

  const wrapped = ((angle + 180) % 360 + 360) % 360 - 180
  return wrapped === -180 && angle > 0 ? 180 : wrapped
}

export const getHitLocationPosition = (
  hit?: Pick<INRWorkoutSessionHit, 'angle' | 'height'>,
): HitLocationPosition | null => {
  if (hit == null || !Number.isFinite(hit.height) || !Number.isFinite(hit.angle)) return null

  const height = clamp(hit.height, 0, 1)
  const angle = wrapAngle(hit.angle)

  return {
    left: ((angle + 180) / 360) * 100,
    outOfRange: hit.height < 0 || hit.height > 1 || hit.angle < -180 || hit.angle > 180,
    top: (1 - height) * 100,
  }
}

const formatValue = (value: number, suffix = ''): string => (
  Number.isFinite(value) ? `${Number(value.toFixed(2))}${suffix}` : 'Invalid'
)

const DEGREE = '\u00b0'
const EMPTY_VALUE = '\u2014'

const HitLocationSurface = ({ hit }: { hit?: INRWorkoutSessionHit }) => {
  const position = getHitLocationPosition(hit)
  const hasInvalidLocation = hit != null && position == null

  return (
    <section className="HitLocation" data-testid="hit-location-surface">
      <header className="HitLocation__header">
        <h3>
          Hit location <span>(experimental)</span>
        </h3>
        {position?.outOfRange && <strong className="HitLocation__warning">Out of range</strong>}
      </header>

      <div className="HitLocation__body">
        <div className="HitLocation__heightLabels" aria-hidden="true">
          <span>Top <strong>1</strong></span>
          <span>Middle <strong>0.5</strong></span>
          <span>Bottom <strong>0</strong></span>
        </div>

        <div
          aria-label="Boxing bag hit-location map"
          className="HitLocation__plot"
          role="img"
        >
          <span className="HitLocation__zoneLabel HitLocation__zoneLabel--backLeft">Back</span>
          <span className="HitLocation__zoneLabel HitLocation__zoneLabel--sideLeft">Side</span>
          <span className="HitLocation__zoneLabel HitLocation__zoneLabel--front">Front</span>
          <span className="HitLocation__zoneLabel HitLocation__zoneLabel--sideRight">Side</span>
          <span className="HitLocation__zoneLabel HitLocation__zoneLabel--backRight">Back</span>

          {HEIGHT_DIVIDERS.map((height) => (
            <span
              aria-label={`Height boundary ${height}`}
              className="HitLocation__divider HitLocation__divider--horizontal"
              key={height}
              style={{ top: `${(1 - height) * 100}%` }}
            />
          ))}
          {ANGLE_DIVIDERS.map((angle) => (
            <span
              aria-label={`Angle boundary ${angle} degrees`}
              className="HitLocation__divider HitLocation__divider--vertical"
              key={angle}
              style={{ left: `${((angle + 180) / 360) * 100}%` }}
            />
          ))}

          {hit == null && (
            <div className="HitLocation__empty">Hit the bag to see the location</div>
          )}
          {hasInvalidLocation && (
            <div className="HitLocation__empty HitLocation__empty--invalid">Location unavailable</div>
          )}
          {hit != null && position != null && (
            <span
              aria-label={`Hit at height ${formatValue(hit.height)}, angle ${formatValue(hit.angle, ' degrees')}, force ${formatValue(hit.force)}`}
              className="HitLocation__marker"
              data-testid="hit-location-marker"
              key={hit.punchedAt}
              style={{ left: `${position.left}%`, top: `${position.top}%` }}
            />
          )}
        </div>
      </div>

      <div className="HitLocation__angleLabels" aria-hidden="true">
        <span>Back <strong>{`-180${DEGREE}`}</strong></span>
        <span><strong>{`0${DEGREE}`}</strong></span>
        <span>Back <strong>{`+180${DEGREE}`}</strong></span>
      </div>

      <footer className="HitLocation__readout" aria-live="polite">
        <span>Height <strong data-testid="hit-location-height">{hit ? formatValue(hit.height) : EMPTY_VALUE}</strong></span>
        <span>Angle <strong data-testid="hit-location-angle">{hit ? formatValue(hit.angle, DEGREE) : EMPTY_VALUE}</strong></span>
        <span>Force <strong data-testid="hit-location-force">{hit ? formatValue(hit.force) : EMPTY_VALUE}</strong></span>
      </footer>
    </section>
  )
}

export default HitLocationSurface
